import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import "./combobox.css";

const Combobox = ({
  options,
  value,
  onChange,
  placeholder,
  className,
  disabled,
  isOptionDisabled,
  getOptionLabel,
  getOptionValue,
  renderOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const filteredOptions = options?.filter((option) => {
    // Ensure getOptionLabel always returns a string before calling toLowerCase()
    const label = (getOptionLabel(option) || "").toString();
    return label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const calculateDropdownPosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  }, []);

  // Wrap handleSelectOption in useCallback for stability
  const handleSelectOption = useCallback(
    (option) => {
      onChange(getOptionValue(option));
      // Ensure label is a string before setting searchTerm
      setSearchTerm((getOptionLabel(option) || "").toString());
      setIsOpen(false); // Close dropdown after selection
    },
    [onChange, getOptionValue, getOptionLabel, setSearchTerm, setIsOpen]
  );

  useEffect(() => {
    // Capture the current ref values at the time the effect runs
    const currentWrapperRef = wrapperRef.current;
    const currentDropdownRef = dropdownRef.current;
    let currentScrollableParent = null; // Also capture for scrollable parent logic

    const handleClickOutside = (event) => {
      if (
        currentWrapperRef &&
        !currentWrapperRef.contains(event.target) &&
        currentDropdownRef &&
        !currentDropdownRef.contains(event.target)
      ) {
        setIsOpen(false);
        if (value !== null && value !== undefined) {
          const selectedOption = options.find(
            (opt) => getOptionValue(opt) === value
          );
          if (selectedOption) {
            setSearchTerm((getOptionLabel(selectedOption) || "").toString());
          }
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    if (isOpen) {
      calculateDropdownPosition();
      window.addEventListener("scroll", calculateDropdownPosition);
      window.addEventListener("resize", calculateDropdownPosition);

      let scrollableParentCheck = currentWrapperRef; // Start check from the captured ref
      while (scrollableParentCheck && scrollableParentCheck !== document.body) {
        const style = getComputedStyle(scrollableParentCheck);
        const hasScrollableOverflow =
          (style.overflowY !== "visible" && style.overflowY !== "") ||
          (style.overflowX !== "visible" && style.overflowX !== "");

        if (
          hasScrollableOverflow &&
          (scrollableParentCheck.scrollHeight >
            scrollableParentCheck.clientHeight ||
            scrollableParentCheck.scrollWidth >
              scrollableParentCheck.clientWidth)
        ) {
          currentScrollableParent = scrollableParentCheck; // Capture the found scrollable parent
          currentScrollableParent.addEventListener(
            "scroll",
            calculateDropdownPosition
          );
          break;
        }
        scrollableParentCheck = scrollableParentCheck.parentElement;
      }
      // Store the captured scrollable parent on the ref for cleanup
      // Using a property on currentWrapperRef to persist it across renders for cleanup
      currentWrapperRef._scrollableParent = currentScrollableParent;
    } else {
      window.removeEventListener("scroll", calculateDropdownPosition);
      window.removeEventListener("resize", calculateDropdownPosition);
      if (currentWrapperRef && currentWrapperRef._scrollableParent) {
        currentWrapperRef._scrollableParent.removeEventListener(
          "scroll",
          calculateDropdownPosition
        );
        currentWrapperRef._scrollableParent = null; // Clear the reference
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", calculateDropdownPosition);
      window.removeEventListener("resize", calculateDropdownPosition);
      // Use the captured scrollableParent in cleanup
      if (currentWrapperRef && currentWrapperRef._scrollableParent) {
        currentWrapperRef._scrollableParent.removeEventListener(
          "scroll",
          calculateDropdownPosition
        );
      }
    };
  }, [
    isOpen,
    calculateDropdownPosition,
    options,
    value,
    getOptionValue,
    getOptionLabel,
    // No need to include wrapperRef or dropdownRef here directly, as their .current values are captured
  ]);

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
    calculateDropdownPosition();
  };

  const handleInputClick = () => {
    setIsOpen(true);
    calculateDropdownPosition();
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex(
          (prevIndex) => (prevIndex + 1) % filteredOptions.length
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex(
          (prevIndex) =>
            (prevIndex - 1 + filteredOptions.length) % filteredOptions.length
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        } else if (
          filteredOptions.length === 1 &&
          searchTerm.toLowerCase() ===
            (getOptionLabel(filteredOptions[0]) || "").toString().toLowerCase()
        ) {
          handleSelectOption(filteredOptions[0]);
        }
        setIsOpen(false);
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
      calculateDropdownPosition();
    },
    [
      filteredOptions,
      highlightedIndex,
      searchTerm,
      handleSelectOption,
      getOptionLabel,
      calculateDropdownPosition,
    ]
  );

  useEffect(() => {
    if (value !== null && value !== undefined && options.length > 0) {
      const selectedOption = options.find(
        (option) =>
          getOptionValue(option) ===
          (typeof value === "object" && value !== null ? value.id : value)
      );

      if (selectedOption) {
        setSearchTerm((getOptionLabel(selectedOption) || "").toString());
      } else {
        setSearchTerm("");
      }
    } else if (value === null || value === undefined) {
      setSearchTerm("");
    }
  }, [value, options, getOptionLabel, getOptionValue]);

  return (
    <div className={`combobox-wrapper ${className || ""}`} ref={wrapperRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onClick={handleInputClick}
        onFocus={handleInputClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className="combobox-input"
      />
      {isOpen &&
        createPortal(
          <ul
            className="combobox-options"
            style={dropdownStyle}
            ref={dropdownRef}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                // Determine if the current option is disabled
                const isDisabled = isOptionDisabled && isOptionDisabled(option);

                return (
                  <li
                    key={getOptionValue(option)}
                    className={`combobox-option ${
                      isDisabled ? "disabled-option" : ""
                    } ${index === highlightedIndex ? "highlighted" : ""}`}
                    onClick={() => !isDisabled && handleSelectOption(option)}
                  >
                    {renderOption
                      ? renderOption(option)
                      : (getOptionLabel(option) || "").toString()}
                  </li>
                );
              })
            ) : (
              <li className="combobox-no-results">No results found</li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default Combobox;